import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, FileText, AlertTriangle, BadgeCheck, ShieldQuestion } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CAT = { medico: "Medico / Dottore", infermiere: "Infermiere", fisioterapista: "Fisioterapista", oss: "OSS", assistente_infermieristico: "Assistente infermieristico", trasporto_infermi: "Ambulanza / Trasporto infermi", badante: "Badante" };
const ST = { PENDING: ["Da approvare", "bg-amber-100 text-amber-800"], UNDER_REVIEW: ["In revisione", "bg-amber-100 text-amber-800"], VERIFIED: ["Approvato", "bg-emerald-100 text-emerald-800"], REJECTED: ["Rifiutato", "bg-rose-100 text-rose-700"], SUSPENDED: ["Sospeso", "bg-rose-100 text-rose-700"], EXPIRED: ["Scaduto", "bg-slate-100 text-slate-600"] };
const ROLE_FOR = { medico: "medico", infermiere: "infermiere" };
const DOC_LABEL = { identita: "Identità", identita_fronte: "Identità fronte", identita_retro: "Identità retro", tessera_ordine: "Tessera ordine", albo: "Albo", altro: "Altro" };
const MISSING = { identita_fronte: "fronte del documento d'identità", identita_retro: "retro del documento d'identità", tessera_ordine: "fronte della tessera dell'ordine", albo: "iscrizione all'albo" };
const METHOD = { didit: "Didit", spid: "SPID", cie: "CIE" };

function IdentityBadge({ o }) {
  const ids = o.identity || [];
  if (!ids.length) {
    return <span data-testid={`operator-identity-${o.user_id}`} className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600"><ShieldQuestion className="h-3.5 w-3.5" />Identità non verificata</span>;
  }
  return ids.map((i) => (
    <span key={i.method} data-testid={`operator-identity-${o.user_id}-${i.method}`} title={`Verificata il ${fmtDateTime(i.verified_at)}`} className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800">
      <BadgeCheck className="h-3.5 w-3.5" />Identità verificata · {METHOD[i.method] || i.method}{i.sandbox && " (sandbox)"}
    </span>
  ));
}

function OperatorRow({ o, reload }) {
  const [note, setNote] = useState("");
  const v = o.verification;
  const docs = v?.documents || [];
  const missing = v?.missing_docs || ["identita_fronte", "identita_retro", "albo"];
  const idOk = (o.identity || []).length > 0;
  const ready = v && !missing.length && idOk;
  const pending = !v || ["PENDING", "UNDER_REVIEW"].includes(v.status);
  const [label, cls] = v ? ST[v.status] : ["Documenti mancanti", "bg-slate-100 text-slate-600"];
  const decide = async (status) => {
    try {
      await api.post(`/admin/verifications/${v.id}/decision`, { status, note, valid_months: 12, assign_role: status === "VERIFIED" ? ROLE_FOR[o.usage_type] || null : null });
      toast.success(status === "VERIFIED" ? "Operatore approvato" : "Operatore rifiutato");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const openDoc = async (d) => {
    const res = await api.get(`/admin/verifications/${v.id}/document/${d.id}`, { responseType: "blob" });
    window.open(URL.createObjectURL(res.data));
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`operator-row-${o.user_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">{o.name} · <span className="text-rose-600">{CAT[o.usage_type]}</span></p>
          <p className="text-xs text-slate-500">{o.email} · registrato il {fmtDateTime(o.registered_at)}{v ? ` · CF ${v.credential.codice_fiscale} · ${v.credential.degree}${v.credential.albo_number ? ` · Albo ${v.credential.albo_number} (${v.credential.order_name})` : ""}` : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <IdentityBadge o={o} />
          <span data-testid={`operator-status-${o.user_id}`} className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${cls}`}>{label}</span>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {missing.map((t) => <span key={t} className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800"><AlertTriangle className="h-3 w-3" />Manca {MISSING[t]}</span>)}
        {docs.map((d) => <button key={d.id} data-testid={`operator-doc-${d.id}`} onClick={() => openDoc(d)} className="flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-700 hover:border-emerald-400"><FileText className="h-3 w-3" />{DOC_LABEL[d.doc_type] || "Altro"}: {d.filename}</button>)}
      </div>
      {v && pending && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input className="h-8 max-w-xs text-xs" data-testid={`operator-note-${o.user_id}`} placeholder="Nota per l'operatore (facoltativa)" value={note} onChange={(e) => setNote(e.target.value)} />
          {!idOk && <span className="text-[11px] text-amber-700" data-testid={`operator-approve-hint-${o.user_id}`}>Approvazione possibile dopo la verifica dell'identità</span>}
          <Button size="sm" disabled={!ready} data-testid={`operator-approve-${o.user_id}`} onClick={() => decide("VERIFIED")} className="bg-emerald-600 hover:bg-emerald-700"><Check className="mr-1 h-3.5 w-3.5" />Approva</Button>
          <Button size="sm" variant="outline" data-testid={`operator-reject-${o.user_id}`} onClick={() => decide("REJECTED")} className="text-rose-700"><X className="mr-1 h-3.5 w-3.5" />Rifiuta</Button>
        </div>
      )}
    </div>
  );
}

export function OperatorsApproval() {
  const [items, setItems] = useState([]);
  const [all, setAll] = useState(false);
  const [idf, setIdf] = useState("tutti");
  const load = useCallback(() => api.get("/admin/operators", { params: { status: all ? "all" : "pending" } }).then(({ data }) => setItems(data)), [all]);
  useEffect(() => {
    load();
  }, [load]);
  const shown = idf === "tutti" ? items : items.filter((o) => (idf === "verificata") === !!(o.identity || []).length);
  return (
    <div className="space-y-3" data-testid="operators-approval">
      <div className="flex gap-2">
        {[[false, "Da approvare"], [true, "Tutti gli operatori"]].map(([k, l]) => <button key={l} data-testid={`operators-filter-${k ? "all" : "pending"}`} onClick={() => setAll(k)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${all === k ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
      </div>
      <div className="flex flex-wrap gap-2">
        {[["tutti", "Tutte le identità"], ["verificata", "Identità verificata"], ["da_verificare", "Da verificare"]].map(([k, l]) => <button key={k} data-testid={`operators-identity-filter-${k}`} onClick={() => setIdf(k)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${idf === k ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>{l} ({k === "tutti" ? items.length : items.filter((o) => (k === "verificata") === !!(o.identity || []).length).length})</button>)}
      </div>
      {!shown.length && <p className="text-sm text-slate-500" data-testid="operators-empty">Nessun operatore {all ? "registrato" : "in attesa di approvazione"}.</p>}
      {shown.map((o) => <OperatorRow key={o.user_id} o={o} reload={load} />)}
    </div>
  );
}
